// Image preloading utilities for performance optimization
import { useEffect } from 'react';

// Critical images that should be preloaded immediately
export const CRITICAL_IMAGES = {
  FAVICON: '/favicon.ico',
} as const;

// Route-specific images for intelligent preloading - reduced for mobile
export const ROUTE_IMAGES = {
  '/': [], // No preloading needed - webpack handles image optimization
  '/about': [],
  '/contact': [],
  '/search': [],
  '/dashboard': [],
} as const;

// Check if user is on mobile/slow connection
const isMobileOrSlowConnection = () => {
  // Check for mobile device
  const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Check for slow connection
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
  
  return isMobile || isSlowConnection;
};

// Preload a single image with mobile optimization
export const preloadImage = (src: string, priority: 'high' | 'low' = 'low'): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Skip preloading on mobile for non-critical images
    if (isMobileOrSlowConnection() && priority === 'low') {
      resolve();
      return;
    }

    const img = new Image();
    
    // Set priority for modern browsers
    if ('loading' in img) {
      img.loading = priority === 'high' ? 'eager' : 'lazy';
    }
    
    // Add timeout to prevent hanging
    const timeout = setTimeout(() => {
      reject(new Error('Image preload timeout'));
    }, 5000);
    
    img.onload = () => {
      clearTimeout(timeout);
      resolve();
    };
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Image failed to load'));
    };
    img.src = src;
  });
};

// Preload multiple images with mobile optimization
export const preloadImages = async (images: string[], priority: 'high' | 'low' = 'low'): Promise<void> => {
  // Skip bulk preloading on mobile
  if (isMobileOrSlowConnection() && priority === 'low') {
    return;
  }

  try {
    // Limit concurrent preloads on mobile
    const maxConcurrent = isMobileOrSlowConnection() ? 2 : 4;
    const chunks = [];
    
    for (let i = 0; i < images.length; i += maxConcurrent) {
      chunks.push(images.slice(i, i + maxConcurrent));
    }
    
    for (const chunk of chunks) {
      await Promise.allSettled(chunk.map(src => preloadImage(src, priority)));
    }
  } catch (error) {
   
  }
};

// Hook for route-based image preloading with mobile optimization
export const useImagePreloader = (currentPath: string) => {
  useEffect(() => {
    // Only preload critical images immediately (currently just favicon)
    const imagesToPreload = [CRITICAL_IMAGES.FAVICON];
    
    if (imagesToPreload.length > 0) {
      preloadImages([...imagesToPreload], 'high');
    }
    
    // Skip predictive preloading on mobile/slow connections
    if (isMobileOrSlowConnection()) {
      return;
    }
    
    // Preload images for likely next routes with low priority (desktop only)
    const probableNextRoutes = getProbableNextRoutes(currentPath);
    probableNextRoutes.forEach(route => {
      const routeImages = ROUTE_IMAGES[route as keyof typeof ROUTE_IMAGES] || [];
      if (routeImages.length > 0) {
        setTimeout(() => preloadImages([...routeImages], 'low'), 3000); // Increased delay
      }
    });
  }, [currentPath]);
};

// Predict likely next routes based on current location
const getProbableNextRoutes = (currentPath: string): string[] => {
  const routePredictions: Record<string, string[]> = {
    '/': ['/search', '/about', '/contact'],
    '/search': ['/contact', '/about'],
    '/about': ['/contact', '/search'],
    '/contact': ['/search', '/about'],
    '/signin': ['/dashboard', '/'],
    '/signup': ['/signin', '/'],
  };
  
  return routePredictions[currentPath] || [];
};

// Create resource hints for better performance
export const createResourceHints = (images: string[]) => {
  images.forEach(src => {
    // Create preload link element
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = src;
    preloadLink.type = 'image/png';
    
    // Add to document head if not already present
    const existingLink = document.querySelector(`link[href="${src}"]`);
    if (!existingLink) {
      document.head.appendChild(preloadLink);
    }
  });
};

// Enhanced lazy loading with intersection observer - mobile optimized
export const useLazyLoading = () => {
  useEffect(() => {
    // Use more aggressive lazy loading on mobile
    const rootMargin = isMobileOrSlowConnection() ? '10px 0px' : '50px 0px';
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          
          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    }, {
      rootMargin,
      threshold: 0.1
    });

    // Observe all images with data-src attribute
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => imageObserver.observe(img));

    return () => imageObserver.disconnect();
  }, []);
};